import { utils } from 'ethers'
import { type ReactElement, useState, useEffect } from 'react'
import { Grid, Box, Accordion, AccordionSummary, Radio } from '@mui/material'
import { Body2White } from '@/components/typography'
import { useCurrentChain } from '@/hooks/useChains'
import AmountWithIcon from '@/components/common/AmountWithIcon'
import type { MetaTransactionData } from '@safe-global/safe-core-sdk-types' //TransactionOptions
import use4337Pack from '@/hooks/use4337Pack'
import useBalances from '@/hooks/useBalances'
import { safeParseUnits } from '@/utils/formatters'

const FeeSection = ({
  metaTxData,
  onError,
  onFee,
  isShowError = true,
}: {
  metaTxData: MetaTransactionData | undefined
  onError: (error: string) => void
  onFee: ({ feeType, feeAmount }: { feeType: string; feeAmount: string }) => void
  isShowError?: boolean
}): ReactElement => {
  const [currentMeta, setCurrentMeta] = useState<MetaTransactionData | undefined>(metaTxData)
  const [feeType, setFeeType] = useState(0)
  const [nativeFee, setNativeFee] = useState('')
  // const [nativeFeeUSD, setNativeFeeUSD] = useState('0.01')
  const [ihrFee, setIhrFee] = useState('0.05')
  const [feeError, setFeeError] = useState('') // 'Insufficient balance to pay the fee'
  const chainInfo = useCurrentChain()
  const erc4337Pack = use4337Pack()
  const { balances, loading } = useBalances()

  useEffect(() => {
    if (!nativeFee || nativeFee === '') return

    const nativeBalance = balances.items.find((item) => parseInt(item.tokenInfo.address, 16) === 0)
    if (!nativeBalance) {
      setFeeError('Insufficient balance to pay the fee')
      onError('Insufficient balance to pay the fee')
    } else {
      if (safeParseUnits(nativeFee, nativeBalance.tokenInfo.decimals)?.gt(nativeBalance.balance)) {
        setFeeError('Insufficient balance to pay the fee')
        onError('Insufficient balance to pay the fee')
        return
      }
      setFeeError('')
      onError('')
    }
  }, [balances, loading, nativeFee])

  useEffect(() => {
    if (erc4337Pack) {
      if (metaTxData === undefined) {
        return
      }
      setFeeError('')
      if (currentMeta !== metaTxData) {
        setNativeFee('')
        onFee({ feeType: 'native', feeAmount: '' })
      }
      setCurrentMeta(metaTxData)

      erc4337Pack
        .createTransaction({ transactions: [metaTxData] })
        .then((safeOperation) => {
          const { callGasLimit, maxFeePerGas } = safeOperation.data
          const fee = utils.formatEther(callGasLimit * maxFeePerGas)
          setNativeFee(fee.slice(0, 8))
          onFee({ feeType: 'native', feeAmount: fee })
        })
        .catch((error) => {
          setFeeError('Failed to estimate fee')
          onError('Failed to estimate fee')
        })
    }
  }, [erc4337Pack, metaTxData])

  const onExpand = (id: number) => (_: any, isExpanded: boolean) => {
    if (isExpanded) setFeeType(0)
  }

  return (
    <Grid container>
      <Grid item xs={12} mt="16px">
        <Box>
          <Body2White
            sx={{
              color: 'var(--Ebony-Clay-400, #AAB3CF)',
              fontWeight: 'bold',
            }}
          >
            Estimated Fee
          </Body2White>
          <Box display="flex" flexDirection="column" flexWrap="wrap" justifyContent="center" alignItems="center" mt={1}>
            <Accordion
              key={0}
              expanded={feeType === 0}
              onChange={onExpand(0)}
              sx={{
                '&.MuiAccordion-root:hover>.MuiAccordionSummary-root': {
                  background: 'none',
                },
                '&.MuiAccordion-root.Mui-expanded': {
                  border: '1px solid var(--Blue-Ribbon-600, #5D5DFF)',
                  marginBottom: '8px',
                },
                borderRadius: '6px',
              }}
            >
              <AccordionSummary
                sx={{ paddingLeft: 0, background: 'none' }}
                aria-controls="panel1-content"
                id="panel1-header"
              >
                <Box display="flex" flexDirection="row" justifyContent="center" alignItems="center">
                  <Radio
                    sx={{ mx: '8px', '&.MuiRadio-root.Mui-checked': { color: 'var(--Blue-Ribbon-600, #5D5DFF)' } }}
                    checked={feeType === 0}
                  />
                  <Box py="8px">
                    <AmountWithIcon
                      uiAmount={nativeFee}
                      symbol={chainInfo?.nativeCurrency.symbol || 'MATIC'}
                      logoUri={chainInfo?.nativeCurrency.logoUri || ''}
                    />
                  </Box>
                </Box>
              </AccordionSummary>
            </Accordion>
            <Accordion
              key={1}
              expanded={feeType === 1}
              onChange={onExpand(1)}
              sx={{
                '&.MuiAccordion-root:hover>.MuiAccordionSummary-root': {
                  background: 'none',
                },
                '&.MuiAccordion-root.Mui-expanded': {
                  border: '1px solid var(--Blue-Ribbon-600, #5D5DFF)',
                  marginBottom: '8px',
                },
                borderRadius: '6px',
              }}
            >
              <AccordionSummary
                sx={{ paddingLeft: 0, background: 'none' }}
                aria-controls="panel2-content"
                id="panel2-header"
              >
                <Box
                  sx={{ width: '100%' }}
                  display="flex"
                  flexDirection="row"
                  justifyContent="center"
                  alignItems="center"
                >
                  <Radio
                    sx={{ mx: '8px', '&.MuiRadio-root.Mui-checked': { color: 'var(--Blue-Ribbon-600, #5D5DFF)' } }}
                    checked={feeType === 1}
                  />
                  <Grid container py="8px" sx={{ width: '100%' }}>
                    <Grid item xs="auto">
                      <AmountWithIcon
                        uiAmount={ihrFee}
                        symbol={'IHR'}
                        logoUri={'https://inheritwallet.io/images/logos/full-color/32x32.png'}
                      />
                    </Grid>
                    <Body2White sx={{ fontWeight: '400', color: 'var(--Ebony-Clay-500, #7C8AB4)', marginLeft: '16px' }}>
                      (Coming soon)
                    </Body2White>
                  </Grid>
                </Box>
              </AccordionSummary>
            </Accordion>
          </Box>
        </Box>
      </Grid>
      {isShowError && feeError !== '' && (
        <Body2White sx={{ color: 'var(--Supporting-Carnation, #FF2239)', marginTop: '8px' }}>{feeError}</Body2White>
      )}
    </Grid>
  )
}

export default FeeSection
